import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import Fetch from "../../services/Fetch";
import PageContent from "../../components/PageContent";
import { useConstants } from "../../hooks/UseConstants";
import { Box, Chip, Divider, Grid, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import PeopleIcon from '@mui/icons-material/People';
import PlayLessonIcon from '@mui/icons-material/PlayLesson';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';
import DashboardStatCard from "../../components/cards/DashboardStatCard";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import { useNavigate } from "react-router-dom";
import ScrollReveal from "../../components/motion/ScrollReveal";
import LogoImage from "../../images/logo/logo.png";
import { APP_NAME } from "../../constants/brand";
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import { formatCurrency } from "../../helper/formatCurrency";

function formatNumber(value, locale) {
    return new Intl.NumberFormat(locale).format(Number(value || 0));
}

function formatMonthLabel(label, month, isAr) {
    if (label) return label;
    const [year, monthNum] = String(month || '').split('-');
    if (!year || !monthNum) return '—';
    const date = new Date(Number(year), Number(monthNum) - 1, 1);
    return new Intl.DateTimeFormat(isAr ? 'ar-EG' : 'en-US', {
        month: 'short',
        year: 'numeric',
    }).format(date);
}

function Dashboard() {
    const navigate = useNavigate();
    const theme = useTheme();
    const { host, language } = useConstants();
    const isAr = language === 'ar';
    const { wait, profile } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);

    const loadData = async () => {
        const dashRes = await Fetch(host + '/reports/teacher-dashboard', 'GET', null);
        if (dashRes.status === 200 && dashRes.data?.data) {
            setStats(dashRes.data.data);
        } else {
            setStats({
                cards: { students: 0, courses: 0, paths: 0, balance: 0, total_earnings: 0 },
                monthly_chart: [],
                top_courses: [],
                recent_sales: [],
            });
        }
        setLoading(false);
    };

    useEffect(() => {
        if (!wait) loadData();
    }, [wait]);

    const cards = stats?.cards;
    const displayName = profile?.first_name || (isAr ? 'مدرس' : 'Teacher');
    const locale = isAr ? 'ar-EG' : 'en-US';
    const monthlyChart = stats?.monthly_chart || [];
    const maxChart = Math.max(...(monthlyChart.map((m) => Number(m.total || 0)) || [1]), 1);
    const monthlyTotal = useMemo(
        () => monthlyChart.reduce((sum, item) => sum + Number(item.total || 0), 0),
        [monthlyChart]
    );
    const bestMonth = useMemo(() => {
        if (!monthlyChart.length) return null;
        return [...monthlyChart].sort((a, b) => Number(b.total || 0) - Number(a.total || 0))[0];
    }, [monthlyChart]);

    const statCards = [
        {
            label: isAr ? 'إجمالي الطلاب' : 'Total Students',
            value: formatNumber(cards?.students ?? 0, locale),
            icon: <PeopleIcon />,
            accent: 'blue',
            onClick: () => navigate('/students')
        },
        {
            label: isAr ? 'الدورات' : 'Courses',
            value: formatNumber(cards?.courses ?? 0, locale),
            icon: <PlayLessonIcon />,
            accent: 'sky',
            onClick: () => navigate('/my-courses')
        },
        {
            label: isAr ? 'المسارات' : 'Paths',
            value: formatNumber(cards?.paths ?? 0, locale),
            icon: <FeaturedPlayListIcon />,
            accent: 'teal',
            onClick: () => navigate('/my-paths')
        },
        {
            label: isAr ? 'الرصيد المتاح' : 'Available Balance',
            value: formatCurrency(cards?.balance ?? 0, isAr),
            icon: <AccountBalanceWalletIcon />,
            accent: 'indigo',
            onClick: () => navigate('/financial')
        },
        {
            label: isAr ? 'إجمالي الأرباح' : 'Total Earnings',
            value: formatCurrency(cards?.total_earnings ?? 0, isAr),
            icon: <TrendingUpIcon />,
            accent: 'blue',
            onClick: () => navigate('/financial')
        },
    ];

    return (
        <PageContent wait={wait} loading={loading}>
            <Box
                className="home-hero-banner"
                sx={{
                    position: 'relative',
                    borderRadius: 4,
                    overflow: 'hidden',
                    mb: 3,
                    minHeight: { xs: 220, md: 200 },
                    color: '#fff',
                    boxShadow: '0 20px 50px rgba(13, 53, 140, 0.18)',
                }}
            >
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #0D358C 0%, #1a5bb8 42%, #457b9d 78%, #040E26 100%)' }} />
                <Box sx={{
                    position: 'absolute', width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.06)',
                    top: -70, insetInlineEnd: -50, animation: 'homeFloat 7s ease-in-out infinite',
                }} />
                <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 3, md: 4 }, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
                    <Box className="hero-logo-card">
                        <Box component="img" src={LogoImage} alt={APP_NAME} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography variant="h4" fontWeight={800} sx={{ fontSize: { xs: '1.5rem', md: '1.85rem' }, lineHeight: 1.25 }}>
                            {APP_NAME}
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.92, mt: 1, lineHeight: 1.7, maxWidth: 520 }}>
                            {isAr
                                ? 'لوحة المدرس — تابع طلابك، أرباحك، ودوراتك من مكان واحد.'
                                : 'Teacher hub — track students, earnings, and courses in one place.'}
                        </Typography>
                        <Chip
                            label={isAr ? `مرحباً، ${displayName}` : `Welcome, ${displayName}`}
                            sx={{ mt: 1.5, bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700 }}
                        />
                    </Box>
                </Box>
            </Box>

            <ScrollReveal>
                <Box className="flex justify-between flex-wrap gap-2 mb-3" dir={isAr ? 'rtl' : 'ltr'}>
                    {statCards.map((card, i) => (
                        <DashboardStatCard key={i} {...card} className="flex-[1_1_18%] min-w-[140px] mx-0 max-sm:flex-[1_1_45%]" />
                    ))}
                </Box>
            </ScrollReveal>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <ScrollReveal delay={80}>
                        <DashboardPanelCard
                            title={isAr ? 'الأرباح الشهرية' : 'Monthly earnings'}
                            subtitle={isAr ? 'آخر الأشهر المسجلة في مبيعاتك' : 'Your recorded earnings over recent months'}
                            className="mx-0 h-full"
                        >
                            {monthlyChart.length ? (
                                <Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        <Chip
                                            icon={<ReceiptLongOutlinedIcon />}
                                            label={`${isAr ? 'إجمالي الفترة' : 'Period total'}: ${formatCurrency(monthlyTotal, isAr)}`}
                                            sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700 }}
                                        />
                                        {bestMonth ? (
                                            <Chip
                                                icon={<ArrowUpwardOutlinedIcon />}
                                                label={`${isAr ? 'أفضل شهر' : 'Best month'}: ${formatMonthLabel(bestMonth.label, bestMonth.month, isAr)}`}
                                                sx={{ bgcolor: '#f0fdf4', color: '#166534', fontWeight: 700 }}
                                            />
                                        ) : null}
                                    </Box>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {monthlyChart.map((item) => (
                                            <Box key={item.month}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <CalendarMonthOutlinedIcon sx={{ color: '#0D358C', fontSize: 18 }} />
                                                        <Typography variant="body2" fontWeight={700}>
                                                            {formatMonthLabel(item.label, item.month, isAr)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" fontWeight={700} sx={{ color: '#0D358C' }}>
                                                        {formatCurrency(item.total, isAr)}
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={maxChart > 0 ? Math.max((Number(item.total || 0) / maxChart) * 100, Number(item.total || 0) > 0 ? 6 : 0) : 0}
                                                    sx={{
                                                        height: 10,
                                                        borderRadius: 999,
                                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#e2e8f0',
                                                        '& .MuiLinearProgress-bar': {
                                                            borderRadius: 999,
                                                            background: 'linear-gradient(90deg, #0D358C 0%, #2563EB 100%)',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            ) : (
                                <Typography color="text.secondary" className="text-center py-8">
                                    {isAr ? 'لا توجد بيانات' : 'No data'}
                                </Typography>
                            )}
                        </DashboardPanelCard>
                    </ScrollReveal>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <ScrollReveal delay={120}>
                        <DashboardPanelCard
                            title={isAr ? 'أكثر الدورات مبيعاً' : 'Top-selling courses'}
                            subtitle={isAr ? 'أفضل الدورات أداءً حسب عدد المبيعات' : 'Your best-performing courses by sales count'}
                            className="mx-0 h-full"
                        >
                            {stats?.top_courses?.length ? stats.top_courses.map((c, i) => {
                                const maxSales = Math.max(...stats.top_courses.map((row) => Number(row.sales || 0)), 1);
                                return (
                                    <Box key={c.id} sx={{ py: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.75 }}>
                                            <Typography variant="body2" fontWeight={700}>
                                                {i + 1}. {isAr ? (c.name_ar || c.name_en) : (c.name_en || c.name_ar)}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={`${formatNumber(c.sales, locale)} ${isAr ? 'مبيعة' : 'sales'}`}
                                                sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700 }}
                                            />
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={Math.max((Number(c.sales || 0) / maxSales) * 100, Number(c.sales || 0) > 0 ? 8 : 0)}
                                            sx={{
                                                height: 8,
                                                borderRadius: 999,
                                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#e2e8f0',
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 999,
                                                    background: 'linear-gradient(90deg, #0D358C 0%, #2563EB 100%)',
                                                },
                                            }}
                                        />
                                        {i < stats.top_courses.length - 1 ? <Divider sx={{ mt: 1.5 }} /> : null}
                                    </Box>
                                );
                            }) : (
                                <Typography color="text.secondary">{isAr ? 'لا توجد مبيعات بعد' : 'No sales yet'}</Typography>
                            )}
                        </DashboardPanelCard>
                    </ScrollReveal>
                </Grid>
            </Grid>

            {stats?.recent_sales?.length > 0 && (
                <ScrollReveal delay={160}>
                    <DashboardPanelCard title={isAr ? 'آخر المبيعات' : 'Recent Sales'} className="mx-0 mt-3">
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{isAr ? 'الطالب' : 'Student'}</TableCell>
                                        <TableCell>{isAr ? 'الدورة' : 'Course'}</TableCell>
                                        <TableCell>{isAr ? 'المبلغ' : 'Amount'}</TableCell>
                                        <TableCell>{isAr ? 'ربحك' : 'Your Profit'}</TableCell>
                                        <TableCell>{isAr ? 'التاريخ' : 'Date'}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {stats.recent_sales.map((sale) => (
                                        <TableRow key={sale.id}>
                                            <TableCell>{sale.student}</TableCell>
                                            <TableCell>{isAr ? (sale.name_ar || sale.name_en) : (sale.name_en || sale.name_ar)}</TableCell>
                                            <TableCell>{formatCurrency(sale.amount, isAr)}</TableCell>
                                            <TableCell sx={{ color: '#166534', fontWeight: 700 }}>{formatCurrency(sale.teacher_profit, isAr)}</TableCell>
                                            <TableCell>
                                                {sale.date
                                                    ? new Intl.DateTimeFormat(isAr ? 'ar-EG' : 'en-US', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                    }).format(new Date(sale.date))
                                                    : '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </DashboardPanelCard>
                </ScrollReveal>
            )}
        </PageContent>
    );
}

export default Dashboard;
